/**
 * HKDF-SHA256 (RFC 5869), backed by Node's built-in `crypto.hkdfSync`
 * (OpenSSL). This is a reviewed implementation of the standard extract-and-
 * expand construction — no bespoke KDF is defined here. The public signature is
 * backend-agnostic (`Uint8Array` in/out) and is equally implementable in the
 * browser via `libsodium`'s `crypto_kdf_hkdf_sha256_*` or WebCrypto.
 */
import { hkdfSync } from 'node:crypto';
import { FumeCryptoError } from './errors.js';
import { asBuffer } from './internal.js';
const SHA256_OUTPUT = 32;
const HKDF_MAX_OUTPUT = 255 * SHA256_OUTPUT;
/**
 * One-shot HKDF-SHA256 = Expand(Extract(salt, ikm), info, length).
 *
 * A `null` salt is treated as a string of HashLen (32) zero bytes, per RFC 5869.
 */
export function hkdfSha256(ikm, salt, info, length) {
    if (!(ikm instanceof Uint8Array)) {
        throw new FumeCryptoError('ikm must be a Uint8Array');
    }
    if (salt !== null && !(salt instanceof Uint8Array)) {
        throw new FumeCryptoError('salt must be a Uint8Array or null');
    }
    if (!(info instanceof Uint8Array)) {
        throw new FumeCryptoError('info must be a Uint8Array');
    }
    if (!Number.isInteger(length) || length <= 0 || length > HKDF_MAX_OUTPUT) {
        throw new FumeCryptoError(`length must be an integer in 1..${HKDF_MAX_OUTPUT}, got ${length}`);
    }
    // RFC 5869: a not-provided salt is set to HashLen zero bytes.
    const effectiveSalt = salt === null ? Buffer.alloc(SHA256_OUTPUT) : asBuffer(salt);
    const okm = hkdfSync('sha256', asBuffer(ikm), effectiveSalt, asBuffer(info), length);
    return new Uint8Array(okm);
}
//# sourceMappingURL=hkdf.js.map